import 'keen-slider/keen-slider.min.css'

import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { Dot } from 'app/components/Common/Dot'
import { Image, Props as ImageProps } from 'app/components/Common/Image'
import { Rumble } from 'app/components/Icons'
import { FlexBox } from 'app/components/Layout/FlexBox'
import { theme } from 'app/theme'
import useMediaQuery from 'app/utils/useMediaQuery'
import { rgba } from 'emotion-rgba'
import { useKeenSlider } from 'keen-slider/react'
import { uniq } from 'lodash'
import React, { memo, useEffect, useRef, useState } from 'react'
import { useInView } from 'react-intersection-observer'
import { Parallax } from 'react-scroll-parallax'

const AUTOPLAY_TIMER = 5000

export interface Props {
  claim?: string
  images?: ImageProps[]
  mobileImages?: ImageProps[]
  payoff?: string
  siteName?: string
  variant?: Variant
}

export const Hero = memo(function Hero({
  claim,
  images,
  mobileImages,
  payoff,
  siteName,
  variant = 'default',
}: Props) {
  if (!images) {
    return null
  }

  if (images.length < 1) {
    return null
  }

  const [animObjectRef, isAnimObjectVisible] = useInView({ triggerOnce: true })

  const isMobile = useMediaQuery('(max-width: 1199px)')
  const [sliderImages, setSliderImages] = useState<any>(
    isMobile ? mobileImages : images,
  )

  const [currentSlide, setCurrentSlide] = useState(0)
  const [details, setDetails] = useState<any>(null)
  const [loaded, setLoaded] = useState<boolean[]>([])
  const [nextLoaded, setNextLoaded] = useState<boolean[]>([])
  const timer = useRef<any>()

  function positionStyle(idx: any) {
    if (!details) {
      return undefined
    }

    const winWidth = window.innerWidth

    const position = details.slides[idx].distance
    const x = (instanceRef.current?.size || winWidth) * -position
    return {
      transform: `translateX(${x + position * (winWidth / 2)}px)`,
      WebkitTransform: `translateX(${x + position * (winWidth / 2)}px)`,
    }
  }

  const [sliderRef, instanceRef] = useKeenSlider({
    defaultAnimation: {
      duration: 1500,
    },
    loop: images.length > 1 ? true : false,
    slideChanged(s) {
      setCurrentSlide(s.track.details.rel)
    },
    detailsChanged(s) {
      images.length > 1 ? setDetails(s.track.details) : null
    },
  })

  useEffect(() => {
    isMobile ? setSliderImages(mobileImages) : setSliderImages(images)

    const newLoaded = [...loaded]
    newLoaded[currentSlide] = true
    const newNextLoaded = [...nextLoaded]
    newNextLoaded[currentSlide + 1] = true

    setLoaded(newLoaded)
    setNextLoaded(newNextLoaded)

    timer.current = setInterval(() => {
      if (instanceRef) {
        instanceRef.current?.next()
      }
    }, AUTOPLAY_TIMER)

    return () => {
      clearInterval(timer.current)
    }
  }, [currentSlide, instanceRef, sliderRef, sliderImages, isMobile])

  return (
    <Container>
      <Parallax className="wrapper" translateY={['-20%', '20%']}>
        <Slider ref={sliderRef}>
          {uniq(sliderImages).map((item: any, index) => (
            <Slide
              key={index}
              className={`keen-slider__slide${
                currentSlide === index ? ' active' : ''
              }`}
            >
              <Inner style={positionStyle(index)}>
                <Image
                  alt={loaded[index] || nextLoaded[index] ? item.alt : ''}
                  {...(loaded[index] || nextLoaded[index] ? item : null)}
                />
              </Inner>
            </Slide>
          ))}
        </Slider>
      </Parallax>

      <Wrapper
        className={isAnimObjectVisible ? 'visible' : undefined}
        ref={animObjectRef}
        variant={variant}
      >
        {variant === 'default' ? <Rumble /> : null}

        {payoff ? <Payoff>{payoff}</Payoff> : null}

        {claim ? <Claim dangerouslySetInnerHTML={{ __html: claim }} /> : null}
      </Wrapper>

      {variant === 'home' ? (
        <Bottom
          className={isAnimObjectVisible ? 'visible' : undefined}
          dial={4}
          ref={animObjectRef}
          row
        >
          <Counter>{`${currentSlide + 1}/${images.length}`}</Counter>

          <Rumble />

          {siteName ? <SiteName>{siteName}</SiteName> : null}

          {images.length > 1 ? (
            <Dots dial={5} row>
              {images.map((_item, index) => (
                <Dot
                  key={index}
                  className={currentSlide === index ? 'active' : undefined}
                  onClick={() => {
                    instanceRef.current?.moveToIdx(index)
                  }}
                />
              ))}
            </Dots>
          ) : null}
        </Bottom>
      ) : null}
    </Container>
  )
})

const Container = styled.section`
  height: 100vh;
  max-height: -webkit-fill-available;
  background: ${({ theme }) => theme.colors.variants.primaryDark};
  overflow: hidden;
  position: relative;
  &:before {
    content: '';
    width: 100%;
    height: 100%;
    backface-visibility: hidden;
    background: linear-gradient(
      ${rgba(theme.colors.variants.neutralDark1, 0.6)},
      ${rgba(theme.colors.variants.neutralDark1, 0.1)},
      ${rgba(theme.colors.variants.neutralDark1, 0.6)}
    );
    position: absolute;
    top: 0;
    left: 0;
    z-index: 3;
  }

  .wrapper {
    height: 100%;
    position: relative;
    z-index: 2;
  }
`

const Slider = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  outline: none;
  overflow: hidden;
  position: absolute;
  top: 0;
  left: 0;
`

const Slide = styled.div`
  overflow: hidden;
  position: relative;
  &.active {
    img {
      transition-delay: 0s;
      transform: none !important;
    }
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    position: absolute;
    top: 0;
    left: 0;
    transition: transform 1s 1.5s ease-out;
    transform-origin: center left;
  }
`

const Inner = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  transition-timing-function: cubic-bezier(0.85, 0, 0.15, 1);
`

const Payoff = styled.h1`
  font-family: ${({ theme }) => theme.fontFamily.heading};
  font-size: 5rem;
  line-height: 6rem;

  @media (max-width: 1199px) {
    font-size: 3.25rem;
    line-height: 3.875rem;
  }
`

const Claim = styled.h2`
  font-family: ${({ theme }) => theme.fontFamily.paragraph};
  font-size: 1rem;
  font-weight: 300;
  line-height: 1.375rem;
  margin-top: 0.375rem;
`

const Wrapper = styled.div<ContainerProps>`
  color: ${({ theme }) => theme.colors.variants.neutralLight5};
  opacity: 0;
  position: absolute;
  bottom: 7.5rem;
  right: 8.333vw;
  left: 8.333vw;
  text-align: center;
  transform: translateY(3.125rem);
  transition: 0.8s 1.2s ease-in-out;
  z-index: 3;
  &.visible {
    opacity: 1;
    transform: none;
  }

  > svg {
    width: 0.375rem;
    height: 0.375rem;
    fill: ${({ theme }) => theme.colors.variants.neutralLight5};
    margin-bottom: 0.375rem;
  }

  @media (max-width: 1199px) {
    bottom: 5rem;
    right: 1.875rem;
    left: 1.875rem;
  }

  ${({ variant }) => {
    switch (variant) {
      case 'home':
        return css`
          bottom: 9.75rem;
          text-align: left;

          ${Claim} {
            font-family: ${theme.fontFamily.heading};
            font-size: 5rem;
            font-weight: 400;
            line-height: 6rem;
            margin-top: 0;
          }

          @media (max-width: 1199px) {
            opacity: 1 !important;
            bottom: 8.75rem;
            transform: none !important;

            ${Claim} {
              font-size: 2.25rem;
              line-height: 3.25rem;
            }
          }
        `
    }
  }}
`

const Bottom = styled(FlexBox)`
  color: ${({ theme }) => theme.colors.variants.neutralLight5};
  font-family: ${({ theme }) => theme.fontFamily.paragraph};
  font-size: 1rem;
  font-weight: 300;
  line-height: 1.375rem;
  opacity: 0;
  position: absolute;
  bottom: 5.75rem;
  right: 8.333vw;
  left: 8.333vw;
  transform: translateY(calc(50% + 3.125rem));
  transition: 0.8s 1.4s ease-in-out;
  z-index: 3;
  &.visible {
    opacity: 1;
    transform: none;
  }

  svg {
    width: 0.375rem;
    height: 0.375rem;
    fill: ${({ theme }) => theme.colors.variants.neutralLight5};
    margin: 0 1.25rem 0 1rem;
  }

  @media (max-width: 1199px) {
    opacity: 1 !important;
    bottom: 5rem;
    right: 1.875rem;
    left: 1.875rem;
    transform: none !important;

    svg {
      display: none;
    }
  }
`

const Counter = styled.div`
  min-width: 1.625rem;
`

const SiteName = styled.div`
  @media (max-width: 1199px) {
    display: none;
  }
`

const Dots = styled(FlexBox)`
  margin-left: auto;
`

interface ContainerProps {
  variant: Variant
}

export type Variant = 'default' | 'home'
